<template>
    <div class="kallelsemall-form">
        <div class="header">
            <h1>{{ heading }}</h1>

            <div class="grid-container">
                <div class="grid-item-span-6">
                    <div>
                        <strong>Typ av mall</strong><br />
                        <span>Operation</span>
                    </div>
                    <br />
                    <KForm
                        ref="formRef"
                        id="kallelsemall-form"
                        :initial-values-getter="initForm"
                        submit-content="Spara"
                        cancel-content="Avbryt"
                        :form-errors="formErrors"
                        :errors="inputErrors"
                        @submit="submitForm"
                        @cancel="cancelForm"
                    >
                        <template #default>
                            <div class="form-content">
                                <div class="form-section">
                                    <KText
                                        type="text"
                                        name="namn"
                                        label="Namn på mall"
                                        validation="required"
                                        suffixText="Max 40 tecken"
                                        autocomplete="off"
                                        class="span-three"
                                    />
                                </div>

                                <KEditor name="body" label="Innehall" class="span-three" />
                            </div>
                        </template>
                    </KForm>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, onBeforeMount, ref, toRefs } from "vue";
    import cloneDeep from "lodash/cloneDeep";
    import KForm from "@/components/forms/Form/KForm.vue";
    import { getErrorMessage } from "@/api.js";
    import { flashMessage, openDialog } from "@/utils/index.js";
    import emitter from "tiny-emitter/instance";
    import KEditor from "@/components/forms/inputs/Editor/KEditor.vue";
    import { useStore } from "@/store";
    import { useKForm } from "@/composables/useKForm";
    import type { AxiosError } from "axios";

    type KallelseBody = Record<string, unknown> | string | null;
    type KallelsemallFormData = {
        namn: string;
        body?: KallelseBody;
    };

    const DEFAULTVALUES: KallelsemallFormData = {
        namn: "",
        body: null,
    };

    const store = useStore();

    const props = defineProps<{
        tabId: number;
        parameters?: {
            id?: number | string;
            namn?: string;
            body?: unknown;
            isEditing?: boolean;
            isDuplicating?: boolean;
        };
    }>();

    const { tabId, parameters } = toRefs(props);
    const formRef = ref();
    const { inputErrors, formErrors, handleErrorResponse } = useKForm();

    const heading = computed(() => {
        if (parameters?.value?.isEditing) {
            return "Redigera kallelsemall";
        }
        if (parameters?.value?.isDuplicating) {
            return "Duplicera kallelsemall";
        }
        return "Skapa kallelsemall";
    });

    const hasUnsavedData = computed(() => formRef.value?.hasUnsavedData());

    defineExpose({
        hasUnsavedData,
        tabId,
    });

    const setTabName = () => {
        store.commit("tabs/setTabName", {
            id: tabId.value,
            name: heading.value,
        });
    };

    const initForm = async () => {
        const initialValues = cloneDeep(DEFAULTVALUES) as KallelsemallFormData;

        if (parameters?.value && (parameters.value.isEditing || parameters.value.isDuplicating)) {
            initialValues.body = (parameters.value.body ?? null) as KallelseBody;
            initialValues.namn = parameters.value.namn ?? "";
        }

        return initialValues;
    };

    const closeTab = ({ override, hasUnsavedData }: { override?: boolean; hasUnsavedData?: boolean } = {}) => {
        if (!override && hasUnsavedData) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    {
                        title: "Fortsätt redigera",
                        type: "secondary",
                    },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => {
                            store.dispatch("tabs/closeTab", tabId.value);
                        },
                    },
                ],
            });

            return;
        }

        store.dispatch("tabs/closeTab", tabId.value);
    };

    const submitForm = async (formData: unknown) => {
        const submittedData = formData as KallelsemallFormData;
        const payload = {
            ...submittedData,
            typ: "operation",
            body: submittedData.body,
        };

        if (parameters?.value?.isDuplicating && payload.body === null) {
            payload.body = (parameters.value?.body ?? null) as KallelseBody;
        }

        await store.dispatch("tabs/displaySpinner", { id: tabId.value, display: true });

        try {
            const response = parameters?.value?.id
                ? await store.dispatch("kallelsemall/updateKallelsemall", {
                      kallelsemall: { id: parameters.value.id, ...payload },
                  })
                : await store.dispatch("kallelsemall/createKallelsemall", { kallelsemall: payload });

            const status = (response as { status?: number } | null)?.status;
            const isUpdated = status ? status === 200 : Boolean((response as { id?: string | number } | null)?.id);
            const isCreated = status ? status === 201 : Boolean((response as { id?: string | number } | null)?.id);

            if (isUpdated || isCreated) {
                flashMessage(isCreated ? "Kallelsemall skapad" : "Kallelsemall redigerad");

                emitter.emit("kallelsemall:changed");

                await store.dispatch("tabs/closeTab", tabId.value);

                return;
            }

            throw new Error("Fel vid skapande/redigering av kallelsemall");
        } catch (error) {
            const typedError = error as AxiosError;

            if (typedError?.response?.data) {
                handleErrorResponse(typedError);
                return;
            }

            openDialog(getErrorMessage(typedError), "error");
        } finally {
            await store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
        }
    };

    const cancelForm = ({ hasUnsavedData }: { hasUnsavedData: boolean }) => {
        closeTab({ hasUnsavedData });
    };

    onBeforeMount(() => {
        setTabName();
    });
</script>

<style lang="scss" scoped>
    .kallelsemall-form {
        position: relative;

        .grid-container {
            display: flex;
            flex-wrap: wrap;

            .grid-item {
                flex-basis: calc(100% / 12);
                padding: 10px;
            }

            .grid-item-span-6 {
                flex-basis: calc(100% / 12 * 6); /* Set the width to span 6 columns */
            }
        }
    }
</style>
